import { getCSSVariable } from "./css-util.js";

/**
 * To fill the chart, when we are using a gradient that becomes
 * transparent at y=0, we need to know the relative position of this
 * zero.
 * This method gets the zero position as a ratio between 0 (top
 * of the chart) and 1 (bottom of the chart), so the gradient can
 * always be visible, whatever the range of the data.
 *
 * @param {number[]} data - List of Y values of the chart points
 */
export function getZeroPositionRatio({ data }) {
	const min = Math.min(...data);
	if (min >= 0) return 1;

	const max = Math.max(...data);
	if (max <= 0) return 0;

	const range = Math.abs(max - min);

	return 1 - Math.abs(min / range);
}

/**
 * Vertical gradient starting from the top (i.e., 0) to the
 * zeroPositionRatio. Its intensity can be adjusted with
 * yAxisEndPoint.
 *
 * @param {CanvasRenderingContext2D} context - Interface provided by
 * the html canvas element on which the chart is drawn
 * @param {color} color - Color from which starts the gradient
 * @param {float} zeroPositionRatio - Relative position where the
 * gradient becomes transparent. It must be between 0 (top of the graph) and
 * 1 (bottom of the graph)
 * @param {integer} yAxisEndPoint - Relative position where the
 * gradient becomes the more colorful, stretching or tightening the
 * intensity of the gradient color. Changing it brights or darkens the
 * gradient. e.g., 100 means the max color is reached on top of the
 * chart, 90 a bit after (the gradient is brighter), 120 a bit before
 * (the gradient is darker).
 */
export function makePositiveGradient({
	context,
	color,
	zeroPositionRatio = 1,
	yAxisEndPoint = 100,
}) {
	const gradient = context.createLinearGradient(0, 0, 0, yAxisEndPoint);
	gradient.addColorStop(0, color);
	gradient.addColorStop(zeroPositionRatio, getTransparent());
	return gradient;
}

/**
 * Vertical gradient starting from the bottom (i.e., 1) to the
 * zeroPositionRatio. Its intensity can be adjusted with
 * yAxisEndPoint.
 *
 * @param {CanvasRenderingContext2D} context - Interface provided by
 * the html canvas element on which the chart is drawn
 * @param {color} color - Color from which starts the gradient
 * @param {float} zeroPositionRatio - Relative position where the
 * gradient becomes transparent. It must be between 0 (top of the graph) and
 * 1 (bottom of the graph)
 * @param {integer} yAxisEndPoint - Relative position where the
 * gradient becomes the more colorful, stretching or tightening the
 * intensity of the gradient color. Changing it brights or darkens the
 * gradient. e.g., 100 means the max color is reached on the bottom of the
 * chart, 90 a bit after (the gradient is brighter), 120 a bit before
 * (the gradient is darker).
 */
export function makeNegativeGradient({
	context,
	color,
	zeroPositionRatio = 0,
	yAxisEndPoint = 100,
}) {
	const gradient = context.createLinearGradient(0, 0, 0, yAxisEndPoint);
	gradient.addColorStop(zeroPositionRatio, getTransparent());
	gradient.addColorStop(1, color);
	return gradient;
}

/**
 * The transparent used in the gradient. It must be a transparent
 * white, or the gradient will look all dark and muddy
 */
function getTransparent() {
	return getCSSVariable("--chart-transparent-white");
}
